import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Clock } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { api, errMsg } from "../lib/api";
import { Logo } from "../components/Bits2";

const HERO = "https://images.pexels.com/photos/5217889/pexels-photo-5217889.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200";

const Shell = ({ title, sub, children }) => (
  <div className="grid min-h-screen lg:grid-cols-[1.1fr_1fr]">
    <div className="relative hidden lg:block bg-slate-950">
      <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
      <div className="relative flex h-full flex-col justify-between p-12 text-white">
        <Logo light size="h-11 w-11" />
        <div className="max-w-md">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-blue-300 mb-4">Ekol Temizlik · Saha Satış</p>
          <h2 className="font-display text-4xl font-bold leading-tight">Temizlik ürünleri kataloğu, iskonto ve sipariş — tek ekranda, sahada.</h2>
        </div>
      </div>
    </div>
    <div className="flex items-center justify-center px-6 py-12 bg-[hsl(210,40%,98%)]">
      <div className="w-full max-w-sm fade-up">
        <div className="mb-8 lg:hidden"><Logo /></div>
        <h1 className="font-display text-3xl font-bold tracking-tight">{title}</h1>
        <p className="mt-2 mb-8 text-sm text-slate-500">{sub}</p>
        {children}
      </div>
    </div>
  </div>
);

export function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const u = await login(f.email, f.password);
      nav(u.role === "admin" ? "/" : "/siparis-olustur");
    } catch (ex) { setErr(errMsg(ex)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Giriş Yap" sub="Hesabınızla oturum açın.">
      <form onSubmit={submit} className="space-y-4">
        <div><Label>E-posta</Label><Input data-testid="login-email-input" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="mt-1.5 h-11" /></div>
        <div><Label>Şifre</Label><Input data-testid="login-password-input" type="password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="mt-1.5 h-11" /></div>
        {err && <p data-testid="login-error" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{err}</p>}
        <Button data-testid="login-submit-button" disabled={busy} className="h-11 w-full bg-slate-950 hover:bg-blue-600">
          {busy ? "Giriş yapılıyor..." : "Giriş Yap"} <ArrowRight className="ml-1 h-4 w-4" />
        </Button>
      </form>
      <p className="mt-6 text-sm text-slate-500">Hesabınız yok mu? <Link data-testid="login-register-link" to="/kayit" className="font-semibold text-blue-600 hover:underline">Kayıt olun</Link></p>
    </Shell>
  );
}

export function RegisterPage() {
  const [f, setF] = useState({ name: "", email: "", phone: "", password: "" });
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/auth/register", f); setDone(true); } catch (ex) { toast.error(errMsg(ex)); } finally { setBusy(false); }
  };
  if (done) return (
    <Shell title="Kaydınız Alındı" sub="">
      <div data-testid="register-success" className="rounded-xl border border-amber-300 bg-amber-50 p-5 text-sm text-amber-900">
        <Clock className="mb-2 h-5 w-5" />Hesabınız yönetici onayı bekliyor. Onaylandıktan sonra giriş yapabilirsiniz.
      </div>
      <Link to="/giris" data-testid="register-back-login" className="mt-6 inline-block text-sm font-semibold text-blue-600 hover:underline">Giriş sayfasına dön</Link>
    </Shell>
  );
  const fld = (k, label, type = "text", req = true) => (
    <div><Label>{label}</Label><Input data-testid={`register-${k}-input`} type={type} required={req} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1.5 h-11" /></div>
  );
  return (
    <Shell title="Plasiyer Kaydı" sub="Kayıt sonrası hesabınız yönetici onayına düşer.">
      <form onSubmit={submit} className="space-y-4">
        {fld("name", "Ad Soyad")}{fld("email", "E-posta", "email")}{fld("phone", "Telefon", "tel", false)}{fld("password", "Şifre (min. 6 karakter)", "password")}
        <Button data-testid="register-submit-button" disabled={busy} className="h-11 w-full bg-slate-950 hover:bg-blue-600">{busy ? "Gönderiliyor..." : "Kayıt Ol"}</Button>
      </form>
      <p className="mt-6 text-sm text-slate-500">Zaten hesabınız var mı? <Link data-testid="register-login-link" to="/giris" className="font-semibold text-blue-600 hover:underline">Giriş yapın</Link></p>
    </Shell>
  );
}
